import React from 'react';
import { connect } from 'react-redux';
import { isEmpty, map } from 'lodash';
import { v4 as uuid } from 'uuid';

import { SAVE_SETTINGS } from 'constants/actionTypes';
import { settingsSelector } from 'selectors/settings';
import { isDeepEqual } from 'utils/generic';
import settingsValidation from 'validation/settingsValidation';
import { openModal } from 'actions/modalActions';

class SettingsScreen extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      settings: props.settings,
      validationMessages: [],
    };
  }

  componentDidUpdate(prevProps) {
    if (!isDeepEqual(prevProps.settings, this.props.settings)) {
      this.setState({ settings: this.props.settings });
    }
  }

  handleInputChange = (event) => {
    const { target } = event;
    const { name } = target;
    const value = target.type === 'checkbox' ? target.checked : target.value;

    this.setState((prevState) => ({
      settings: {
        ...prevState.settings,
        [name]: value,
      },
    }));
  };

  renderDecimalSeparatorSelect = () => {
    const { settings } = this.state;

    const decimalSeparatorChoices = {
      'Dot (.)': '.',
      'Comma (,)': ',',
    };
    const firstValue = settings.decimalSeparator
      ? settings.decimalSeparator
      : Object.values(decimalSeparatorChoices)[0];

    return this.renderSelect({
      name: 'decimalSeparator',
      choices: decimalSeparatorChoices,
      defaultValue: firstValue,
    });
  };

  renderThousandsSeparatorSelect = () => {
    const { settings } = this.state;

    const thousandsSeparatorChoices = {
      'Dot (.)': '.',
      'Comma (,)': ',',
      'Space ( )': ' ',
    };
    const firstValue = settings.thousandsSeparator
      ? settings.thousandsSeparator
      : Object.values(thousandsSeparatorChoices)[0];

    return this.renderSelect({
      name: 'thousandsSeparator',
      choices: thousandsSeparatorChoices,
      defaultValue: firstValue,
    });
  };

  renderGroupsPrecisionSelect = () => {
    const { settings } = this.state;

    const notRounded = 'Not Rounded';
    const wholeNumber = 'Whole Number';

    const groupsPrecisionChoices = {
      [notRounded]: -1,
      [wholeNumber]: 0,
      Tens: 1,
      Hundreds: 2,
      Thousands: 3,
      Millions: 6,
    };
    const firstValue = settings.groupsPrecision
      ? settings.groupsPrecision
      : Object.values(groupsPrecisionChoices)[0];

    return this.renderSelect({
      name: 'groupsPrecision',
      choices: groupsPrecisionChoices,
      defaultValue: firstValue,
    });
  };

  renderSelect = ({ name, choices, defaultValue }) => (
    <select
      name={name}
      className="settings-screen-dropdown"
      value={defaultValue}
      onChange={this.handleInputChange}
    >
      {map(choices, (value, key) => (
        <option value={value}>{key}</option>
      ))}
    </select>
  );

  renderPrecision = () => (
    <input
      name="precision"
      className="settings-screen-precision"
      autoComplete="off"
      type="number"
      value={this.state.settings.precision}
      onChange={(event) => this.handleInputChange(event)}
      placeholder="Precision"
      min={2}
      max={6}
    />
  );

  renderExchangeRates = () => (
    <input
      type="button"
      className="btn"
      value="Configure"
      onClick={() =>
        this.props.openModal({
          id: uuid(),
          type: 'component',
          componentType: 'ExchangeRatesModal',
        })
      }
    />
  );

  checkValidity = () => {
    const { decimalSeparator, thousandsSeparator } = this.state.settings;
    const validationMessages = [];

    if (
      settingsValidation.isSameSignSelected({
        decimalSeparator,
        thousandsSeparator,
      })
    ) {
      validationMessages.push(
        'Please select different signs for Decimal and Thousands Separator'
      );
    }

    return validationMessages;
  };

  renderValidationMessages = () => {
    const { validationMessages } = this.state;

    return (
      <div className="validation-messages">
        <span className="validation-warning">
          Fix these errors before saving settings:
        </span>
        <ul>
          {validationMessages.map((validationMessage, index) => (
            <li key={index}>{validationMessage}</li>
          ))}
        </ul>
      </div>
    );
  };

  areSettingsChanged = () =>
    !isDeepEqual(this.props.settings, this.state.settings);

  renderButtons = () => {
    return (
      <>
        <input
          type="button"
          className="btn"
          value="Apply"
          onClick={this.onApply}
          disabled={!this.areSettingsChanged()}
        />
        <input
          type="button"
          className="btn"
          value="Discard"
          onClick={this.onDiscard}
          disabled={!this.areSettingsChanged()}
        />
      </>
    );
  };

  onApply = () => {
    const { saveSettings } = this.props;
    const { settings } = this.state;

    const validationMessages = this.checkValidity();
    const hasValidationMessages = !isEmpty(validationMessages);

    if (hasValidationMessages) {
      this.setState({ validationMessages });
    } else {
      this.setState({ validationMessages: [] });
      saveSettings(settings);
    }
  };

  onDiscard = () => {
    const { settings } = this.props;
    this.setState({ settings, validationMessages: [] });
  };

  render() {
    const { validationMessages } = this.state;
    const hasValidationMessages = !isEmpty(validationMessages);

    return (
      <div className="settings-screen">
        <h2>Settings</h2>
        <ul>
          <li>
            <b className="settings-screen-label">Decimal separator:</b>
            {this.renderDecimalSeparatorSelect()}
          </li>
          <li>
            <b className="settings-screen-label">Thousands separator:</b>
            {this.renderThousandsSeparatorSelect()}
          </li>
          <li>
            <b className="settings-screen-label">
              Precision for Groups and Accounts:
            </b>
            {this.renderGroupsPrecisionSelect()}
          </li>
          <li>
            <b className="settings-screen-label">Precision for KPIs:</b>
            {this.renderPrecision()}
          </li>
          <li>
            <b className="settings-screen-label">Exchange rates:</b>
            {this.renderExchangeRates()}
          </li>
        </ul>
        {hasValidationMessages && this.renderValidationMessages()}
        {this.renderButtons()}
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  settings: settingsSelector(state),
});

const mapDispatchToProps = (dispatch) => ({
  saveSettings: (settings) => dispatch({ type: SAVE_SETTINGS, settings }),
  openModal: (modalConfig) => dispatch(openModal(modalConfig)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsScreen);
